import { useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { useMapStore } from '../../store/useMapStore';
import { loadPoints } from '../../data/load';
import type { PointRecord } from '../../types';
import { readURL, writeURL } from '../../logic/url';

export default function Sidebar() {
  const { sidebarOpen, setSidebarOpen, points, setPoints, searchQuery, setSearchQuery, selectedId, setSelectedId, centerOnById, filters, setFilters, clusteringEnabled, setClusteringEnabled } = useMapStore();
  const [sections, setSections] = useState({ factions: false, segmentum: false, type: true, worlds: true });

  useEffect(() => {
    loadPoints().then(setPoints).catch((e) => console.error(e));
  }, [setPoints]);

  useEffect(() => {
    const url = readURL();
    if (url.panel !== undefined) setSidebarOpen(url.panel);
    if (url.id) setSelectedId(url.id);
    if (url.cluster !== undefined) setClusteringEnabled(url.cluster);
    if (url.f) setFilters({ faction: new Set(url.f) });
    if (url.seg) setFilters({ segmentum: new Set(url.seg) });
    if (url.type) setFilters({ type: new Set(url.type) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeURL({ panel: sidebarOpen, id: selectedId ?? undefined, f: Array.from(filters.faction), seg: Array.from(filters.segmentum), type: Array.from(filters.type), cluster: clusteringEnabled });
  }, [sidebarOpen, selectedId, filters, clusteringEnabled]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ']') setSidebarOpen(true);
      if (e.key === '[') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setSidebarOpen]);

  const fuse = useMemo(() => new Fuse(points, { includeScore: true, threshold: 0.3, keys: ['name'] }), [points]);
  const results = useMemo(() => {
    if (!searchQuery) return points.slice(0, 50);
    return fuse.search(searchQuery).slice(0, 50).map((r) => r.item);
  }, [searchQuery, fuse, points]);

  function onSelect(p: PointRecord) {
    setSelectedId(p.id);
    centerOnById(p.id);
  }

  const factions = useMemo(() => Array.from(new Set(points.map((p) => p.faction))).sort(), [points]);

  const selected = useMemo(() => points.find((p) => p.id === selectedId) || null, [points, selectedId]);

  return (
    <div className={`fixed top-0 right-0 h-full z-20 transition-transform ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`} style={{ width: 320 }}>
      <div className="w-full h-full bg-neutral-900/90 backdrop-blur border-l border-neutral-800 flex flex-col overflow-hidden">
        <div className="p-3 flex items-center justify-between border-b border-neutral-800">
          <div className="font-semibold text-sm">{selected ? 'World Details' : 'Search & Filters'}</div>
          <button className="text-xs text-neutral-300 hover:text-white" onClick={() => setSidebarOpen(false)}>Close</button>
        </div>
        {!selected && (
          <>
            <div className="p-3">
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search worlds..."
                className="w-full px-2 py-1 rounded bg-neutral-800 border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="px-3 pb-3 text-xs text-neutral-400">{points.length} worlds loaded</div>
            <div className="px-3 pb-3">
              <label className="flex items-center gap-2 text-xs text-neutral-300">
                <input
                  type="checkbox"
                  className="mr-2 align-middle accent-blue-500"
                  checked={clusteringEnabled}
                  onChange={(e) => setClusteringEnabled(e.target.checked)}
                />
                Cluster nearby worlds
              </label>
            </div>
            <div className="px-3 pb-2">
              <button className="w-full text-left text-xs text-neutral-300 hover:text-white" onClick={() => setSections((s) => ({ ...s, factions: !s.factions }))}>
                {sections.factions ? '▸' : '▾'} Faction Filter
              </button>
              {!sections.factions && (
              <div className="flex flex-wrap gap-2">
                {factions.map((f) => {
                  const checked = filters.faction.has(f);
                  return (
                    <label key={f} className={`text-xs px-2 py-1 rounded border ${checked ? 'bg-neutral-800 border-neutral-600' : 'border-neutral-700'}`}>
                      <input
                        type="checkbox"
                        className="mr-1 align-middle"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(filters.faction);
                          if (e.target.checked) next.add(f); else next.delete(f);
                          setFilters({ faction: next });
                        }}
                      />
                      {f}
                    </label>
                  );
                })}
              </div>
              )}
            </div>
            <div className="px-3 pb-2">
              <button className="w-full text-left text-xs text-neutral-300 hover:text-white" onClick={() => setSections((s) => ({ ...s, segmentum: !s.segmentum }))}>
                {sections.segmentum ? '▸' : '▾'} Segmentum
              </button>
              {!sections.segmentum && (
              <div className="flex flex-wrap gap-2 mt-1">
                {Array.from(new Set(points.map((p) => p.segmentum || 'Unknown'))).sort().map((seg) => {
                  const checked = filters.segmentum.has(seg);
                  return (
                    <label key={seg} className={`text-xs px-2 py-1 rounded border ${checked ? 'bg-neutral-800 border-neutral-600' : 'border-neutral-700'}`}>
                      <input
                        type="checkbox"
                        className="mr-1 align-middle"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(filters.segmentum);
                          if (e.target.checked) next.add(seg); else next.delete(seg);
                          setFilters({ segmentum: next });
                        }}
                      />
                      {seg}
                    </label>
                  );
                })}
              </div>
              )}
            </div>
            <div className="px-3 pb-2">
              <button className="w-full text-left text-xs text-neutral-300 hover:text-white" onClick={() => setSections((s) => ({ ...s, type: !s.type }))}>
                {sections.type ? '▸' : '▾'} World Type
              </button>
              {!sections.type && (
              <div className="flex flex-wrap gap-2 mt-1">
                {Array.from(new Set(points.map((p) => p.type).filter(Boolean) as string[])).sort().slice(0, 100).map((t) => {
                  const checked = filters.type.has(t);
                  return (
                    <label key={t} className={`text-xs px-2 py-1 rounded border ${checked ? 'bg-neutral-800 border-neutral-600' : 'border-neutral-700'}`}>
                      <input
                        type="checkbox"
                        className="mr-1 align-middle"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(filters.type);
                          if (e.target.checked) next.add(t); else next.delete(t);
                          setFilters({ type: next });
                        }}
                      />
                      {t}
                    </label>
                  );
                })}
              </div>
              )}
            </div>
            {searchQuery && (
              <div className="flex-1 overflow-auto divide-y divide-neutral-800">
                {results.map((p) => (
                  <button key={p.id} onClick={() => onSelect(p)} className={`w-full text-left px-3 py-2 hover:bg-neutral-800 ${selectedId === p.id ? 'bg-neutral-800' : ''}`}>
                    <div className="text-sm text-white">{p.name}</div>
                    <div className="text-xs text-neutral-400">{p.faction} {p.subfaction ? `/ ${p.subfaction}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="px-3 pb-2">
              <button className="w-full text-left text-xs text-neutral-300 hover:text-white" onClick={() => setSections((s) => ({ ...s, worlds: !s.worlds }))}>
                {sections.worlds ? '▸' : '▾'} Worlds
              </button>
            </div>
            {!sections.worlds && (
              <div className="flex-1 overflow-auto divide-y divide-neutral-800">
                {[...points]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((p) => (
                  <button key={p.id} onClick={() => onSelect(p)} className={`w-full text-left px-3 py-2 hover:bg-neutral-800 ${selectedId === p.id ? 'bg-neutral-800' : ''}`}>
                    <div className="text-sm text-white">{p.name}</div>
                    <div className="text-xs text-neutral-400">{p.faction} {p.subfaction ? `/ ${p.subfaction}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {selected && (
          <div className="flex-1 overflow-auto">
            <div className="p-3 space-y-2">
              <div className="text-lg font-semibold">{selected.name}</div>
              <div className="text-sm text-neutral-300">{selected.faction}{selected.subfaction ? ` / ${selected.subfaction}` : ''}</div>
              {selected.type && <div className="text-sm text-neutral-300">Type: {selected.type}</div>}
              {selected.segmentum && <div className="text-sm text-neutral-300">Segmentum: {selected.segmentum}</div>}
              {selected.wiki && <a href={selected.wiki} target="_blank" rel="noreferrer" className="text-xs text-blue-400 underline">Lexicanum</a>}
            </div>
            <div className="p-3 border-t border-neutral-800">
              <button className="text-xs text-neutral-300 hover:text-white" onClick={() => setSelectedId(null)}>Back to search</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
